import { ChatWithFeedbackSummaryFooterButtons } from '@modules/chatWithFeedback/components/ChatWithFeedbackSummaryFooterButtons';
import { EmptySummaryContent } from '@modules/chatWithFeedback/components/EmptySummaryContent';
import { FetchErrorMessage } from '@modules/common/components/FetchErrorMessage';
import { WisdomErrorMessage } from '@modules/common/components/WisdomErrorMessage';
import { WisdomProcessingStepsLoader } from '@modules/common/components/WisdomProcessingStepsLoader';
import { WisdomReportIssueButton } from '@modules/common/components/WisdomReportIssueButton';
import {
  DEFAULT_WISDOM_SUMMARY_TIMEOUT,
  NAV_PATHS,
} from '@modules/common/constants';
import { useFetchRecordCount } from '@modules/common/hooks';
import {
  formatNumToStandardNotation,
  getAggregatePeriodFromDateRange,
} from '@modules/common/utils';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useGetDashboardAnalysisLinkWithState } from '@modules/dashboard/hooks';
import { useUpdateFeedQueryStateForGlobalFilters } from '@modules/dashboard/hooks/useUpdateFeedQueryStateForGlobalFilters';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import {
  useGetFeedDuration,
  useGetFeedWhereClause,
  useIsChatWithFeedbackEnabled,
} from '@modules/feed/hooks';
import { useSetAnalysisStoreFromFeed } from '@modules/feed/hooks/useSetAnalysisStoreFromFeed';
import { ArtefactCitationFeedbackList } from '@modules/oracle/components/ArtefactCitationFeedbackList';
import { FeedbackListSummary } from '@modules/oracle/components/FeedbackListSummary';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { useGetFeedbackListSummaryByQueryContainer } from '@modules/oracle/hooks';
import {
  AutorenewRounded,
  ChatBubbleOutlineRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { AnalysisViewFragment, TimeUnit } from 'generated/graphql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect, useMeasure, useToggle } from 'react-use';
import { TimeRange } from 'v2/core/entities';
import { useGetUser } from 'v2/core/services/user';

interface DashboardFeedSummaryProps {
  analysis: AnalysisViewFragment;
}

export function DashboardFeedSummary({ analysis }: DashboardFeedSummaryProps) {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const duration = useGetFeedDuration();
  const whereClause = useGetFeedWhereClause();
  const dispatch = useFeedbackSummaryStore(({ dispatch }) => dispatch);
  const webSocketError = useFeedbackSummaryStore(({ visual }) => visual.error);
  const dashboardDispatch = useDashboardStore(({ dispatch }) => dispatch);

  const { analysisLink } = useGetDashboardAnalysisLinkWithState(analysis);

  useSetAnalysisStoreFromFeed({ analysis });

  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const queryContainer = useMemo(() => {
    const queryPayload = {
      DateRange: duration as TimeRange,
      WhereClause: whereClause,
      AggregatePeriod:
        getAggregatePeriodFromDateRange(duration) ?? TimeUnit.Week,
    };

    return queryPayload;
  }, [duration, whereClause]);

  const prompt = analysis.context.prompt;

  const isReportRoute = window.location.pathname.includes(
    `/${NAV_PATHS.REPORT}/`,
  );

  const {
    reopenWebsocketConnection,
    responseID,
    getFeedbackSummary,
    error,
    isRequestTimedOut,
    isLoading: isGeneratingSummary,
  } = useGetFeedbackListSummaryByQueryContainer({
    queryContainer,
    shouldGetSummaryOnDemand: false,
    prompt,
    timeoutDuration: isReportRoute
      ? 5 * 60 * 1000
      : DEFAULT_WISDOM_SUMMARY_TIMEOUT, // 5 mins for report route and 2 min for dashboard route
  });

  const isError = Boolean(error) || Boolean(webSocketError);

  useDeepCompareEffect(() => {
    reopenWebsocketConnection();
    dispatch({
      type: FeedbackSummaryActionType.RESET,
    });
    setShouldShowLoader(true);
  }, [queryContainer]);

  const regenerateSummary = () => {
    dispatch({
      type: FeedbackSummaryActionType.SET_VISUAL,
      payload: {
        shouldRejectIncomingMessages: false,
      },
    });

    if (prompt) {
      dispatch({
        type: FeedbackSummaryActionType.RESET,
      });
      getFeedbackSummary(prompt);
    }
  };

  useUpdateFeedQueryStateForGlobalFilters();

  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);

  const showFeedbackRecords = () => {
    toggleShouldOpenFeedbackDrawer();
    trackDashboardEvent({
      eventName: 'Dashboard Feed Prompt Answer - See All Records Clicked',
      analysisLink: analysisLink,
      dashboardLink: window.location.href,
    });
  };

  const { data: recordCount } = useFetchRecordCount({ queryContainer });

  const messages = useFeedbackSummaryStore(({ data }) => data.messages);
  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );

  useEffect(() => {
    dashboardDispatch({
      type: DashboardActionType.SET_LOADING_ANALYSES_BY_ID,
      payload: {
        [analysis.id]: !isCompleted,
      },
    });
  }, [analysis.id, dashboardDispatch, isCompleted]);

  const promptRef = useRef<HTMLElement | null>(null);
  const [isPromptTruncated, setIsPromptTruncated] = useState(false);
  const [shouldShowMorePrompt, toggleShouldShowMorePrompt] = useToggle(false);
  const [shouldShowLoader, setShouldShowLoader] = useState(true);

  const [bannerRef, { height: bannerHeight }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    const checkTruncation = () => {
      const element = promptRef.current;
      if (element) {
        setIsPromptTruncated(element.scrollWidth > element.clientWidth);
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [prompt, recordCount, shouldShowMorePrompt]); // Change of both prompt & recordCount can change width of prompt

  const isSummaryLoading = !isCompleted && messages.length === 0 && !isError;
  const isSummaryEmpty = isCompleted && messages.length === 0;
  const isSummaryGenerationInProgress = !isCompleted && messages.length > 0;
  const canRenderSummary = isCompleted || messages.length > 0;

  const renderBanner = () => {
    const getContent = () => {
      if (isChatWithFeedbackEnabled) {
        return (
          <>
            <Stack direction="row" alignItems="center" minWidth={0}>
              {prompt && (
                <Typography
                  variant="caption"
                  color="text.primary"
                  ref={promptRef}
                  sx={{
                    whiteSpace: shouldShowMorePrompt ? 'normal' : 'nowrap',
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1rem',
                    overflowY: 'auto',
                    maxHeight: '50px',
                  }}
                >
                  <Box component="span" color="text.secondary">
                    Prompt:
                  </Box>{' '}
                  {prompt}
                </Typography>
              )}
              {(isPromptTruncated || shouldShowMorePrompt) && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  onClick={toggleShouldShowMorePrompt}
                  sx={{
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {shouldShowMorePrompt ? 'Show less' : 'Show more'}
                </Typography>
              )}
            </Stack>
            {recordCount !== undefined && (
              <Tooltip arrow title="View feedback records" placement="top">
                <Button
                  size="small"
                  onClick={showFeedbackRecords}
                  startIcon={<ChatBubbleOutlineRounded />}
                  sx={{
                    p: 0,
                    '& .MuiButton-startIcon': {
                      mr: 0.7,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.9rem',
                    },
                  }}
                >
                  {formatNumToStandardNotation(recordCount)}
                </Button>
              </Tooltip>
            )}
          </>
        );
      }
      return (
        <>
          <Typography variant="caption" color="text.secondary">
            This summary was generated on {recordCount} feedback records
          </Typography>
          <Button size="small" onClick={showFeedbackRecords}>
            SEE ALL RECORDS
          </Button>
        </>
      );
    };

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          py: 0.5,
          px: 2,
          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        ref={bannerRef}
      >
        {getContent()}
      </Stack>
    );
  };

  const onProgressComplete = useCallback(() => {
    setShouldShowLoader(false);
  }, []);

  const renderContent = () => {
    if (isRequestTimedOut) {
      return (
        <FetchErrorMessage
          onRetry={() => {
            reopenWebsocketConnection();
            dispatch({
              type: FeedbackSummaryActionType.RESET,
            });
          }}
        />
      );
    }

    if (isSummaryLoading || shouldShowLoader) {
      return (
        <WisdomProcessingStepsLoader
          containerSxProps={{ border: 0, px: 3, py: 2.5 }}
          isLoadingComplete={!isSummaryLoading}
          onProgressComplete={onProgressComplete}
          shouldRandomizeProgress
        />
      );
    }

    if (isSummaryEmpty) {
      return (
        <Stack
          width="100%"
          alignItems="center"
          height="100%"
          justifyContent="center"
        >
          <EmptySummaryContent />
        </Stack>
      );
    }

    if (canRenderSummary) {
      return (
        <Box
          height={`calc(100% - ${bannerHeight}px)`}
          sx={{
            display: 'flex',
            p: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            '&:hover': {
              '.dashboard-feed-summary-actions': {
                opacity: 1,
              },
            },
          }}
        >
          <Box width="100%" display="flex" position="relative">
            <FeedbackListSummary
              shouldSkipTypingAnimation={isReportRoute}
              containerProps={{
                sx: {
                  maxHeight: '100%',
                  overflowY: 'auto',
                  px: 1,
                },
              }}
            />
          </Box>
          {!isSummaryLoading && !isSummaryEmpty && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '10px',
                left: 0,
                bgcolor: (theme) =>
                  alpha(theme.palette.background.default, 0.9),
                opacity: 0,
                borderRadius: 1,
                zIndex: 1,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
              }}
              className="dashboard-feed-summary-actions"
            >
              <ChatWithFeedbackSummaryFooterButtons
                responseID={responseID}
                messages={messages}
                prompt={prompt}
                queryContainer={queryContainer}
                onRegenerate={
                  !isSummaryGenerationInProgress ? regenerateSummary : undefined
                }
                viewToken={analysis.viewToken}
              />
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderErrorMessage = () => {
    if (!isError) {
      return null;
    }

    return (
      <Stack
        spacing={1.5}
        p={2}
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <WisdomErrorMessage sx={{ mt: 1, p: 2 }} />
        <Stack direction="row" spacing={1}>
          <LoadingButton
            loading={isGeneratingSummary}
            variant="outlined"
            loadingPosition="start"
            onClick={() => {
              regenerateSummary();
            }}
            startIcon={<AutorenewRounded />}
          >
            Regenerate
          </LoadingButton>
          <WisdomReportIssueButton
            variant="outlined"
            size="medium"
            issueSourceUrl={`${window.location.origin}/${tenantSlug}${analysisLink}`}
            prompt={prompt}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <Box sx={{ height: '100%' }}>
      {renderBanner()}
      {renderContent()}
      {renderErrorMessage()}
      {queryContainer && (
        <ArtefactCitationFeedbackList
          shouldShowFeedbackDrawer={shouldOpenFeedbackDrawer}
          closeFeedbackDrawer={toggleShouldOpenFeedbackDrawer}
          queryContainer={queryContainer}
          defaultPrompt={prompt}
          analysis={{
            ...analysis,
            panels: [],
            reporting: {},
          }}
        />
      )}
    </Box>
  );
}
